/**
 * @description Authenticated Axios client. Session company is X-Company-Id.
 *
 * @dependencies axios, companyHeader, sendableCompanyId
 *
 * @notes A per-request sendable X-Company-Id wins so All can fan out. All is
 *   never sent as a header. Query companyId is not added here.
 */

import axios from 'axios';
import config from './config';
import { getTenantBackendBaseUrl, usesTenantBffOrigin } from '../../utils/lynxBuildHostname';
import { clearTenantResolveCache } from './tenantResolveService';
import { getCompanyHeaderId } from './companyHeader';
import { isSendableCompanyId } from '../../utils/sendableCompanyId';

export function resolveApiClientBaseUrl() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (usesTenantBffOrigin(hostname)) {
    return `${getTenantBackendBaseUrl(hostname)}/api`;
  }
  return config.API_URL;
}

const apiClient = axios.create({
  baseURL: resolveApiClientBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  timeout: config.TIMEOUT || 10000
});

// Re-resolve BFF base URL per request on BFF hostnames (hostname may change without reload).
apiClient.interceptors.request.use((requestConfig) => {
  if (typeof window !== 'undefined' && usesTenantBffOrigin(window.location.hostname)) {
    requestConfig.baseURL = resolveApiClientBaseUrl();
  }
  const existingHeader = requestConfig.headers?.['X-Company-Id']
    ?? requestConfig.headers?.get?.('X-Company-Id');
  const activeCompanyId = isSendableCompanyId(existingHeader)
    ? existingHeader
    : getCompanyHeaderId();
  if (isSendableCompanyId(activeCompanyId)) {
    requestConfig.headers['X-Company-Id'] = activeCompanyId;
  } else {
    delete requestConfig.headers['X-Company-Id'];
  }
  return requestConfig;
});

// Simple retry with exponential backoff for rate limits
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const requestId = error.response?.headers?.['x-request-id'];

    if (requestId) {
      console.error(`API Error [RequestID: ${requestId}]:`, error.message);
      error.requestId = requestId;
    }

    // Session loss — drop Tier-3 resolve cache so reconnect re-queries Postgres.
    if (error.response?.status === 401) {
      clearTenantResolveCache();
    }

    // Handle rate limiting (429) with exponential backoff
    if (error.response?.status === 429 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Get retry delay from header or use default
      const retryAfter = error.response.headers['retry-after'];
      const delay = retryAfter ? parseInt(retryAfter) * 1000 : 2000;

      console.warn(`Rate limited. Retrying after ${delay}ms...`);

      // Wait then retry
      await new Promise(resolve => setTimeout(resolve, delay));
      return apiClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default apiClient;